import { createContext, useContext, useEffect, useRef, useState } from 'react'

interface TableLoadingContextType {
  tableLoading: boolean
  updateTableLoading: (isLoading: boolean) => void
}

const TableLoadingContext = createContext<TableLoadingContextType | null>(null)

/**
 * TableLoadingProvider is a wrapper used for indicating when a table is loading data.
 * It is used by navigation loading indicators and table loading indicators and includes a debounce
 * to prevent flickering of the spinner when tables start and finish loading.
 * First load is not debounced to show the spinner immediately.
 */
export function TableLoadingProvider({ children }: { children: React.ReactNode }) {
  const [tableLoading, setTableLoading] = useState<boolean>(false)
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true)
  const loadingTimeout = useRef<NodeJS.Timeout | null>(null)

  const updateTableLoading = (isLoading: boolean) => {
    if (isFirstLoad) {
      setTableLoading(isLoading)
      setIsFirstLoad(false)
    } else {
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current)
      }
      loadingTimeout.current = setTimeout(() => {
        setTableLoading(isLoading)
      }, 500) // 500ms debounce time
    }
  }

  useEffect(() => {
    return () => {
      // Clear timeout on unmount
      if (loadingTimeout.current) {
        clearTimeout(loadingTimeout.current)
      }
    }
  }, [])

  return (
    <TableLoadingContext.Provider
      value={{
        tableLoading,
        updateTableLoading,
      }}
    >
      {children}
    </TableLoadingContext.Provider>
  )
}
/**
 * Provide context for when a table is loading data, setTableLoading is called in DossTable queries
 * as we are initially fetching and re-fetching data.
 */
export function useTableLoading() {
  const context = useContext(TableLoadingContext)
  if (!context) {
    throw new Error('useTableLoading must be used within a TableLoadingProvider')
  }
  return context
}
